import { useEffect, type ReactNode } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { cx } from './cx';
import styles from './Layout.module.css';

/** Sets the browser tab title ("Invoices · <product>"). */
export function useDocumentTitle(title: string): void {
  const { t } = useI18n();
  useEffect(() => {
    document.title = t.app.pageTitle(title);
  }, [t, title]);
}

export function Page({
  children,
  narrow,
  className,
}: {
  children: ReactNode;
  narrow?: boolean;
  className?: string;
}) {
  return <div className={cx(styles.page, narrow && styles.narrow, className)}>{children}</div>;
}

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
  badges,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  badges?: ReactNode;
}) {
  return (
    <header className={styles.header}>
      <div className={styles.headerText}>
        {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
        <h1 className={styles.title}>{title}</h1>
        {badges ? <div className={styles.actions}>{badges}</div> : null}
        {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
      </div>
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </header>
  );
}

export function Panel({
  title,
  description,
  actions,
  children,
  flush,
  className,
  headingLevel = 2,
  icon,
  id,
  testId,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  className?: string;
  headingLevel?: 2 | 3;
  icon?: ReactNode;
  id?: string;
  testId?: string;
}) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <section
      className={cx(styles.panel, className)}
      aria-labelledby={title && id ? `${id}-title` : undefined}
      id={id}
      data-testid={testId}
    >
      {title ? (
        <div className={styles.panelHeader}>
          <div className={styles.panelTitleGroup}>
            <Heading className={styles.panelTitle} id={id ? `${id}-title` : undefined}>
              {icon}
              {title}
            </Heading>
            {description ? <p className={styles.panelDescription}>{description}</p> : null}
          </div>
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </div>
      ) : null}
      <div className={cx(styles.panelBody, flush && styles.panelBodyFlush)}>{children}</div>
    </section>
  );
}

export function DescriptionList({
  items,
}: {
  items: { term: ReactNode; value: ReactNode; key: string }[];
}) {
  return (
    <dl className={styles.dl}>
      {items.map((item) => (
        <div key={item.key} style={{ display: 'contents' }}>
          <dt>{item.term}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A decimal-string amount, formatted for the current locale (never converted to a number). */
export function Money({
  value,
  currency,
  className,
  testId,
}: {
  value: string | null | undefined;
  currency?: string;
  className?: string;
  testId?: string;
}) {
  const { formatAmount } = useI18n();
  if (value === null || value === undefined || value === '')
    return <span className={cx(styles.muted, className)}>—</span>;
  return (
    <span className={cx(styles.money, className)} data-testid={testId} data-value={value}>
      {formatAmount(value, currency)}
    </span>
  );
}

/** Codes (RIN, ETA item codes, request ids) stay left-to-right inside Arabic text. */
export function Code({ children }: { children: ReactNode }) {
  return <span className={styles.code}>{children}</span>;
}

export function LoadingPage() {
  const { t } = useI18n();
  return (
    <div className={styles.loadingPage} role="status">
      {t.common.loading}
    </div>
  );
}

export const layoutStyles = styles;
